import { abrePDF } from "../../../utils/pdf";
import React, { useState, useEffect, useContext, useMemo } from "react";
import { fDMA } from "../../../utils/fecha";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import {
  Button,
  Row,
  Table,
  Input,
  Label,
  ButtonGroup,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import axios from "axios";
import { TableHeader, Pagination, Search } from "../../../DataTable";
import moment from "moment";
import HistorialCostoModal from "./HistorialCostoModal";

// C1: reporte por artículo en rango de fechas.
// Cantidad vendida (SUM) y costo unitario promedio ponderado por cantidad.
function ReporteArticulosSurtidos() {
  const { user } = useContext(AuthContext);
  const URL_ARTICULOS_SURTIDOS = process.env.REACT_APP_URL_ARTICULOS_SURTIDOS;

  const hoy = new Date();
  const inicio = new Date();
  inicio.setDate(inicio.getDate() - 30);
  const endDate = moment(hoy).format("YYYY-MM-DD");
  const startDate = moment(inicio).format("YYYY-MM-DD");

  const [selectedFechaInicio, setSelectedFechaInicio] = useState(startDate);
  const [selectedFechaFin, setSelectedFechaFin] = useState(endDate);
  // Filtro fiscal: todos | facturados | requieren | facturadosORequieren
  const [filtroFactura, setFiltroFactura] = useState("todos");
  // Con "No", lo pendiente de surtir queda fuera del reporte por completo:
  // no suma cantidad, ni venta, ni costo, ni promedios, y la columna se oculta.
  const [incluirPendientes, setIncluirPendientes] = useState("Si");
  const verPend = incluirPendientes !== "No";

  const [comments, setComments] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState({ field: "", order: "" });

  // C3: modal de detalle de ventas por artículo (mismo endpoint que DashboardVentasTabla)
  const [modalDetalle, setModalDetalle] = useState(false);
  const toggleDetalle = () => setModalDetalle(!modalDetalle);
  const [detalleRows, setDetalleRows] = useState([]);
  const [detalleArticulo, setDetalleArticulo] = useState("");
  const [detalleLoading, setDetalleLoading] = useState(false);
  const [detalleIdArticulo, setDetalleIdArticulo] = useState("");

  // Historial del costo: de dónde sale el costo unitario de una venta
  const [modalCosto, setModalCosto] = useState(false);
  const [historialCosto, setHistorialCosto] = useState(null);
  const [ventaCosto, setVentaCosto] = useState(null);
  const [costoLoading, setCostoLoading] = useState(false);

  const ITEMS_PER_PAGE = 50;

  const fmt = (n) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(Number(n) || 0);
  const fmtQty = (n) =>
    new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(
      Number(n) || 0
    );

  // La URL_ARTICULOS_SURTIDOS típicamente termina en /articulosSurtidos;
  // derivamos la base para llamar al nuevo endpoint articulosSurtidosReporte.
  const apiBase = () =>
    String(URL_ARTICULOS_SURTIDOS || "").replace(/\/articulosSurtidos\/?$/, "");
  const reporteUrl = () =>
    `${apiBase()}/articulosSurtidosReporte/${selectedFechaInicio}/${selectedFechaFin}?filtro=${filtroFactura}&incluirPendientes=${incluirPendientes}`;

  // C3: abrir modal con el detalle de ventas del artículo en el rango actual.
  // Renglón del reporte del artículo abierto, para mostrar al pie del desglose
  // que la suma cuadra con lo que dice la tabla.
  const [detalleRenglon, setDetalleRenglon] = useState(null);

  async function abrirDetalleArticulo(idArticulo, nombre, renglon = null) {
    setDetalleArticulo(nombre || "");
    setDetalleIdArticulo(idArticulo);
    setDetalleRenglon(renglon);
    setDetalleRows([]);
    setDetalleLoading(true);
    setModalDetalle(true);
    try {
      // Mismos filtros que el reporte, para que el desglose sume el renglón.
      const res = await axios.get(
        `${apiBase()}/articulosSurtidosDetalle/${idArticulo}/${selectedFechaInicio}/${selectedFechaFin}?filtro=${filtroFactura}&incluirPendientes=${incluirPendientes}`,
        { headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } }
      );
      setDetalleRows(res.data || []);
    } catch (err) {
      console.log(err);
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo cargar el detalle" });
    } finally {
      setDetalleLoading(false);
    }
  }

  async function abrirHistorialCosto(venta) {
    setVentaCosto(venta);
    setHistorialCosto(null);
    setCostoLoading(true);
    setModalCosto(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_URL_ARTICULOS_EMBARQUES}/historialCosto/${detalleIdArticulo}`,
        { headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } }
      );
      setHistorialCosto(res.data || null);
    } catch (err) {
      console.log(err);
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo cargar el historial del costo" });
      setModalCosto(false);
    } finally {
      setCostoLoading(false);
    }
  }

  // Va en useEffect, NO en useMemo: useMemo es para calcular valores durante el
  // render, no para pedir datos. Con useMemo el estado sobrevivía a los hot
  // reloads sin volver a pedir nada, y la pantalla quedaba mostrando cifras
  // viejas mientras el servidor ya devolvía otras.
  useEffect(() => {
    let cancelado = false;
    axios
      .get(reporteUrl(), {
        headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
      })
      .then((res) => {
        if (cancelado) return; // llegó tarde: ya cambiaron los filtros
        const data = (res.data || []).sort((a, b) =>
          String(a.codigo || "").localeCompare(String(b.codigo || ""))
        );
        setComments(data);
      })
      .catch((err) => console.log(err));
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFechaInicio, selectedFechaFin, filtroFactura, incluirPendientes]);

  const headers = [
    { name: "Código", field: "codigo", sortable: true },
    { name: "Descripción", field: "nombre", sortable: true },
    { name: "Línea", field: "linea", sortable: true },
    { name: "Cant. vendida", field: "cantidadTotal", sortable: true },
    ...(verPend
      ? [{ name: "Pend. surtir", field: "pendienteSurtir", sortable: true }]
      : []),
    { name: "Precio prom.", field: "precioPromedio", sortable: true },
    { name: "IVA prom.", field: "ivaPromedio", sortable: true },
    { name: "Costo prom.", field: "costoPromedio", sortable: true },
    { name: "Venta c/IVA", field: "ventaConIva", sortable: true },
    { name: "Costo total", field: "costoTotal", sortable: true },
    { name: "G. directo", field: "gastoDirecto", sortable: true },
    { name: "Comisión", field: "comision", sortable: true },
    { name: "G. general", field: "gastoGeneral", sortable: true },
    { name: "Utilidad", field: "utilidad", sortable: true },
    { name: "Margen %", field: "margen", sortable: true },
  ];

  const filtrado = useMemo(() => {
    let c = comments;
    if (search) {
      const s = search.toLowerCase();
      c = c.filter(
        (x) =>
          String(x.codigo || "").toLowerCase().includes(s) ||
          String(x.nombre || "").toLowerCase().includes(s) ||
          String(x.linea || "").toLowerCase().includes(s)
      );
    }
    setTotalItems(c.length);
    if (sorting.field) {
      const dir = sorting.order === "asc" ? 1 : -1;
      c = [...c].sort((a, b) => {
        const va = a[sorting.field];
        const vb = b[sorting.field];
        if (typeof va === "number") return dir * (va - vb);
        return dir * String(va || "").localeCompare(String(vb || ""));
      });
    }
    return c.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      (currentPage - 1) * ITEMS_PER_PAGE + ITEMS_PER_PAGE
    );
  }, [comments, currentPage, search, sorting]);

  const totalCantidad = comments.reduce(
    (t, c) => t + (Number(c.cantidadTotal) || 0),
    0
  );
  const totalVenta = comments.reduce(
    (t, c) => t + (Number(c.ventaTotal) || 0),
    0
  );
  const totalCosto = comments.reduce(
    (t, c) => t + (Number(c.costoTotal) || 0),
    0
  );
  const totalPendSurtir = comments.reduce(
    (t, c) => t + (Number(c.pendienteSurtir) || 0),
    0
  );
  const totalIva = comments.reduce((t, c) => t + (Number(c.ivaTotal) || 0), 0);
  const totalGastoDirecto = comments.reduce((t, c) => t + (Number(c.gastoDirecto) || 0), 0);
  const totalComision = comments.reduce((t, c) => t + (Number(c.comision) || 0), 0);
  const totalGastoGeneral = comments.reduce((t, c) => t + (Number(c.gastoGeneral) || 0), 0);
  // La utilidad va sobre la venta CON IVA (oct-2026: el IVA que paga el
  // cliente cuenta como venta), igual que ReporteUtilidades. Este total
  // también es el que cuadra con el TOTAL de ListadoVentas.
  const totalVentaConIva = totalVenta + totalIva;
  // Utilidad = venta c/IVA − costo − gasto directo − comisión − gasto general
  const totalUtilidad = totalVentaConIva - totalCosto - totalGastoDirecto - totalComision - totalGastoGeneral;
  const margenGlobal = totalVentaConIva > 0 ? (totalUtilidad / totalVentaConIva) * 100 : 0;
  const fmtPct = (n) => `${(Number(n) || 0).toFixed(2)}%`;

  const filtroLabel = {
    todos: "Todos",
    facturados: "Facturados",
    requieren: "Requieren factura",
    facturadosORequieren: "Facturados o Requieren",
    noRequieren: "No requieren factura",
    pendientes: "Pendientes",
  }[filtroFactura] || "Todos";

  function PDFTabla() {
    const rows = comments.map((a) => [
      a.codigo,
      a.nombre,
      a.linea,
      fmtQty(a.cantidadTotal),
      ...(verPend ? [fmtQty(a.pendienteSurtir)] : []),
      fmt(a.precioPromedio),
      fmt(a.ivaPromedio),
      fmt(a.costoPromedio),
      fmt(a.ventaConIva),
      fmt(a.costoTotal),
      fmt(a.gastoDirecto),
      fmt(a.comision),
      fmt(a.gastoGeneral),
      fmt(a.utilidad),
      fmtPct(a.margen),
    ]);
    const doc = new jsPDF("landscape");
    doc.text(
      `Reporte Artículos Vendidos  ${selectedFechaInicio} → ${selectedFechaFin}  ·  ${filtroLabel}${verPend ? "" : "  ·  sin pendientes de surtir"}`,
      14,
      14
    );
    doc.autoTable({
      head: [[
        "Código", "Descripción", "Línea", "Cant. vendida",
        ...(verPend ? ["Pend. surtir"] : []),
        "Precio prom.", "IVA prom.", "Costo prom.", "Venta c/IVA", "Costo total",
        "G. directo", "Comisión", "G. general", "Utilidad", "Margen %",
      ]],
      body: rows,
      startY: 20,
      foot: [[
        "", "", "Total",
        fmtQty(totalCantidad),
        ...(verPend ? [fmtQty(totalPendSurtir)] : []),
        "", "", "",
        fmt(totalVentaConIva), fmt(totalCosto),
        fmt(totalGastoDirecto), fmt(totalComision), fmt(totalGastoGeneral),
        fmt(totalUtilidad), fmtPct(margenGlobal),
      ]],
      styles: { fontSize: 8 },
    });
    abrePDF(doc, "ReporteArticulosSurtidos.pdf");
  }

  function excel() {
    const dataExcel = comments.map((a) => ({
      Codigo: a.codigo,
      Descripcion: a.nombre,
      Linea: a.linea,
      Cantidad: Number(a.cantidadTotal || 0),
      ...(verPend ? { PendienteSurtir: Number(a.pendienteSurtir || 0) } : {}),
      PrecioPromedio: Number(a.precioPromedio || 0),
      IvaPromedio: Number(a.ivaPromedio || 0),
      CostoPromedio: Number(a.costoPromedio || 0),
      VentaSinIva: Number(a.ventaTotal || 0),
      Iva: Number(a.ivaTotal || 0),
      VentaConIva: Number(a.ventaConIva || 0),
      CostoTotal: Number(a.costoTotal || 0),
      GastoDirecto: Number(a.gastoDirecto || 0),
      Comision: Number(a.comision || 0),
      GastoGeneral: Number(a.gastoGeneral || 0),
      Utilidad: Number(a.utilidad || 0),
      Margen: Number(a.margen || 0),
    }));
    const option = {
      fileName: `ReporteArticulosSurtidos-${selectedFechaInicio}-${selectedFechaFin}-${filtroFactura}${verPend ? "" : "-sinPendientes"}`,
      datas: [
        {
          sheetData: dataExcel,
          sheetName: "Reporte",
          sheetFilter: [
            "Codigo", "Descripcion", "Linea", "Cantidad",
            ...(verPend ? ["PendienteSurtir"] : []),
            "PrecioPromedio", "IvaPromedio", "CostoPromedio", "VentaSinIva", "Iva", "VentaConIva", "CostoTotal",
            "GastoDirecto", "Comision", "GastoGeneral", "Utilidad", "Margen",
          ],
          sheetHeader: [
            "Código", "Descripción", "Línea", "Cantidad vendida",
            ...(verPend ? ["Pendiente surtir"] : []),
            "Precio promedio", "IVA promedio", "Costo promedio", "Venta sin IVA", "IVA", "Venta con IVA", "Costo total",
            "Gasto directo", "Comisión", "Gasto general", "Utilidad", "Margen %",
          ],
        },
      ],
    };
    new ExportJsonExcel(option).saveExcel();
  }

  return (
    <>
      <Header />
      <br />
      <br />
      {/* El reporte de artículos vendidos es sólo para quien tiene el permiso:
          no basta esconder el botón, la ruta se puede escribir a mano. */}
      {user.reporteArticulosVendidos ? (
        <div className="card col-12">
          <Row>
            <Col md={8}>
              <Button
                size="sm"
                href="/MenuVentas"
                className="btn btn-danger"
                id="botonListado"
              >
                Regresar
              </Button>
            </Col>
            <Col sm={4}>
              <ButtonGroup id="logoutBoton">
                <Button size="sm" className="btn" color="danger" onClick={PDFTabla}>
                  PDF <i className="far fa-file-pdf"></i>
                </Button>
                <Button size="sm" className="btn" color="primary" onClick={excel}>
                  Excel <i className="far fa-file-excel"></i>
                </Button>
              </ButtonGroup>
            </Col>
          </Row>
          <h3 align="center">Reporte Artículos Vendidos</h3>
          <div className="row">
            <div className="col-md-8">
              <Pagination
                total={totalItems}
                itemsPerPage={ITEMS_PER_PAGE}
                currentPage={currentPage}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
            <div className="col-md-4 d-flex flex-row-reverse">
              <Search
                onSearch={(value) => {
                  setSearch(value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>
          <Row>
            <Col md={2}>
              <Label>Fecha Inicio</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaInicio}
                onChange={(e) => setSelectedFechaInicio(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Label>Fecha Fin</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaFin}
                onChange={(e) => setSelectedFechaFin(e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Label>Estado fiscal</Label>
              <Input
                bsSize="sm"
                type="select"
                value={filtroFactura}
                onChange={(e) => {
                  setFiltroFactura(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="todos">Todos</option>
                <option value="facturados">Facturados</option>
                <option value="requieren">Requieren factura</option>
                <option value="facturadosORequieren">Facturados o Requieren</option>
                <option value="noRequieren">No requieren factura</option>
                <option value="pendientes">Pendientes</option>
              </Input>
            </Col>
            <Col md={3}>
              <Label>Pendiente de surtir</Label>
              <Input
                bsSize="sm"
                type="select"
                value={incluirPendientes}
                onChange={(e) => {
                  setIncluirPendientes(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="Si">Incluir en el reporte</option>
                <option value="No">No tomarlo en cuenta</option>
              </Input>
            </Col>
            <Col md={2}>
              <Label>Total cantidad</Label>
              <Input bsSize="sm" value={fmtQty(totalCantidad)} disabled />
            </Col>
            {verPend ? (
              <Col md={2}>
                <Label>Pend. surtir</Label>
                <Input bsSize="sm" value={fmtQty(totalPendSurtir)} disabled />
              </Col>
            ) : null}
            <Col md={2}>
              <Label>Venta sin IVA</Label>
              <Input bsSize="sm" value={fmt(totalVenta)} disabled />
            </Col>
            <Col md={2}>
              <Label>Total IVA</Label>
              <Input bsSize="sm" value={fmt(totalIva)} disabled />
            </Col>
            {/* Este es el que cuadra con el TOTAL de ListadoVentas, siempre y
                cuando los pendientes estén fuera del reporte. */}
            <Col md={2}>
              <Label>Total venta c/IVA</Label>
              <Input bsSize="sm" value={fmt(totalVentaConIva)} disabled />
            </Col>
            <Col md={2}>
              <Label>Total costo</Label>
              <Input bsSize="sm" value={fmt(totalCosto)} disabled />
            </Col>
            <Col md={2}>
              <Label>Gastos (dir. + comisión + gral.)</Label>
              <Input bsSize="sm" value={fmt(totalGastoDirecto + totalComision + totalGastoGeneral)} disabled />
            </Col>
            <Col md={2}>
              <Label>Utilidad</Label>
              <Input bsSize="sm" value={`${fmt(totalUtilidad)} (${fmtPct(margenGlobal)})`} disabled />
            </Col>
          </Row>
          <div style={{ fontSize: 12, color: "#6c757d", margin: "8px 0 0" }}>
            <b>Costo prom.</b> = costo de lo vendido en el rango ÷ piezas. Cada venta se lleva el costo promedio
            que tenía el artículo el día que se surtió, y ese costo cambia cada vez que entra un embarque.
            Por eso, si mueves la fecha de inicio, entran o salen ventas con otro costo y el promedio cambia.
            En el desglose de utilidad puedes dar click en el costo de cada venta para ver de dónde sale.
          </div>
          <br />
          <Table
            size="sm"
            striped
            borderless
            className="table-responsive-xl"
          >
            <TableHeader
              headers={headers}
              onSorting={(field, order) => setSorting({ field, order })}
            />
            <tbody>
              {filtrado.map((a) => (
                <tr key={a.idArticulo}>
                  <td>{a.codigo}</td>
                  <td>{a.nombre}</td>
                  <td>{a.linea}</td>
                  <td align="right">
                    {Number(a.cantidadTotal) > 0 ? (
                      <Button
                        size="sm"
                        color="link"
                        style={{
                          padding: 0,
                          color: "#0d6efd",
                          textDecoration: "underline",
                          fontWeight: 600,
                        }}
                        onClick={() =>
                          abrirDetalleArticulo(
                            a.idArticulo,
                            `${a.codigo || ""} ${a.nombre || ""}`.trim(),
                            a
                          )
                        }
                      >
                        {fmtQty(a.cantidadTotal)}
                      </Button>
                    ) : (
                      fmtQty(a.cantidadTotal)
                    )}
                  </td>
                  {verPend ? (
                    <td align="right">
                      {Number(a.pendienteSurtir) > 0 ? (
                        <span style={{ color: "#b8860b", fontWeight: 600 }}>
                          {fmtQty(a.pendienteSurtir)}
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>
                  ) : null}
                  <td align="right">{fmt(a.precioPromedio)}</td>
                  <td align="right">{fmt(a.ivaPromedio)}</td>
                  <td align="right">{fmt(a.costoPromedio)}</td>
                  <td align="right">{fmt(a.ventaConIva)}</td>
                  <td align="right">{fmt(a.costoTotal)}</td>
                  <td align="right">{fmt(a.gastoDirecto)}</td>
                  <td align="right">{fmt(a.comision)}</td>
                  <td align="right">{fmt(a.gastoGeneral)}</td>
                  <td
                    align="right"
                    style={{ color: Number(a.utilidad) < 0 ? "#dc3545" : "#28a745", fontWeight: 600, whiteSpace: "nowrap" }}
                  >
                    {fmt(a.utilidad)}{" "}
                    {/* Desglose: de dónde sale esta utilidad, renglón por renglón */}
                    <Button
                      size="sm"
                      color="link"
                      title="Ver cómo se calculó esta utilidad"
                      style={{ padding: "0 4px", verticalAlign: "baseline" }}
                      onClick={() =>
                        abrirDetalleArticulo(
                          a.idArticulo,
                          `${a.codigo || ""} ${a.nombre || ""}`.trim(),
                          a
                        )
                      }
                    >
                      <i className="fas fa-search-dollar"></i>
                    </Button>
                  </td>
                  <td
                    align="right"
                    style={{ color: Number(a.margen) < 0 ? "#dc3545" : "#28a745", fontWeight: 600 }}
                  >
                    {fmtPct(a.margen)}
                  </td>
                </tr>
              ))}
              {/* Totales de la tabla */}
              <tr style={{ borderTop: "2px solid #333", fontWeight: "bold" }}>
                <td colSpan={3} align="right">TOTALES</td>
                <td align="right">{fmtQty(totalCantidad)}</td>
                {verPend ? (
                  <td align="right">{fmtQty(totalPendSurtir)}</td>
                ) : null}
                {/* Precio prom. / IVA prom. / Costo prom.: 3 celdas, el IVA
                    total va bajo su columna. Había una celda de más y todo
                    se corría. */}
                <td></td>
                <td align="right">{fmt(totalIva)}</td>
                <td></td>
                <td align="right">{fmt(totalVentaConIva)}</td>
                <td align="right">{fmt(totalCosto)}</td>
                <td align="right">{fmt(totalGastoDirecto)}</td>
                <td align="right">{fmt(totalComision)}</td>
                <td align="right">{fmt(totalGastoGeneral)}</td>
                <td
                  align="right"
                  style={{ color: totalUtilidad < 0 ? "#dc3545" : "#28a745" }}
                >
                  {fmt(totalUtilidad)}
                </td>
                <td
                  align="right"
                  style={{ color: margenGlobal < 0 ? "#dc3545" : "#28a745" }}
                >
                  {fmtPct(margenGlobal)}
                </td>
              </tr>
            </tbody>
          </Table>
          <Pagination
            total={totalItems}
            itemsPerPage={ITEMS_PER_PAGE}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
          />
          {/* C3: modal con el detalle de ventas del artículo elegido */}
          <Modal size="xl" isOpen={modalDetalle} toggle={toggleDetalle} style={{ maxWidth: "96%" }}>
            <ModalHeader toggle={toggleDetalle}>
              <h4>
                Desglose de utilidad — {detalleArticulo}
                <small style={{ display: "block", fontSize: 13, color: "#6c757d", marginTop: 4 }}>
                  {fDMA(selectedFechaInicio)} → {fDMA(selectedFechaFin)} · Estado fiscal: {filtroLabel}
                  {verPend ? " · incluye pendientes de surtir" : " · sin pendientes"}
                </small>
              </h4>
            </ModalHeader>
            <ModalBody>
              <div style={{ fontSize: 13, color: "#555", marginBottom: 10 }}>
                Por renglón: <b>Venta</b> = cantidad × precio × (1 − desc. renglón) × (1 − desc. nota), sin IVA; el{" "}
                <b>IVA</b> se suma a la venta para la utilidad.{" "}
                <b>Costo</b> = cantidad × costo de la venta (click en el costo unitario para ver de dónde sale).{" "}
                <b>Gastos</b>: el directo de la nota (envíos) y la <b>comisión</b> de la nota (pagada o, si aún no, estimada)
                se reparten entre sus renglones por venta (la comisión pagada incluye sus viáticos); el general del periodo entre todas las ventas
                del rango (como ReporteUtilidades).{" "}
                <b>Utilidad</b> = Venta + IVA − Costo − G. directo − Comisión − G. general. Los pendientes de surtir se valúan al precio del pedido
                y al costo actual del artículo, sin gastos.
              </div>
              {detalleLoading ? (
                <div align="center">Cargando…</div>
              ) : detalleRows.length === 0 ? (
                <div align="center">Sin ventas en el rango.</div>
              ) : (
                <Table size="sm" striped bordered className="table-responsive-xl">
                  <thead>
                    <tr style={{ fontSize: 12 }}>
                      <th>Fecha</th>
                      <th>Nota / Pedido</th>
                      <th>Cliente</th>
                      <th align="right">Cant.</th>
                      <th align="right">Precio</th>
                      <th align="right">Desc. renglón</th>
                      <th align="right">Desc. nota</th>
                      <th align="right">Venta</th>
                      <th align="right">IVA</th>
                      <th align="right">Costo unit.</th>
                      <th align="right">Costo</th>
                      <th align="right">G. dir.</th>
                      <th align="right">Comisión</th>
                      <th align="right">G. gral.</th>
                      <th align="right">Utilidad</th>
                      <th align="right">Margen</th>
                    </tr>
                  </thead>
                  <tbody style={{ fontSize: 13 }}>
                    {detalleRows.map((d) => (
                      <tr
                        key={d._id}
                        style={d.pendiente ? { backgroundColor: "#fff3cd" } : undefined}
                        title={d.pendiente ? "Pendiente de surtir: todavía no sale de bodega" : undefined}
                      >
                        <td>{d.fecha ? fDMA(String(d.fecha).slice(0, 10)) : ""}</td>
                        <td>
                          {d.pendiente ? (
                            <span style={{ color: "#856404" }}>
                              <i className="fas fa-clock"></i> Pedido {d.pedido}
                            </span>
                          ) : (
                            <>Nota {d.surtido}</>
                          )}
                        </td>
                        <td>{d.cliente}</td>
                        <td align="right">{new Intl.NumberFormat("en-US").format(d.cantidad)}</td>
                        <td align="right">{fmt(d.precio)}</td>
                        <td align="right">{d.descuento ? `${Number(d.descuento).toFixed(2)}%` : "-"}</td>
                        <td align="right">{d.descuentoNota ? `${Number(d.descuentoNota).toFixed(2)}%` : "-"}</td>
                        <td align="right">{fmt(d.importe)}</td>
                        <td align="right">{fmt(d.iva)}</td>
                        <td align="right">
                          <Button
                            size="sm"
                            color="link"
                            title="Ver de dónde sale este costo"
                            style={{ padding: 0, color: "#0d6efd", textDecoration: "underline", fontWeight: 600 }}
                            onClick={() => abrirHistorialCosto(d)}
                          >
                            {fmt(d.costoUnitario)}
                          </Button>
                        </td>
                        <td align="right">{fmt(d.costoTotal)}</td>
                        <td align="right">{fmt(d.gastoDirecto)}</td>
                        <td align="right">{fmt(d.comision)}</td>
                        <td align="right">{fmt(d.gastoGeneral)}</td>
                        <td align="right" style={{ color: Number(d.utilidad) < 0 ? "#dc3545" : "#28a745", fontWeight: 600 }}>
                          {fmt(d.utilidad)}
                        </td>
                        <td align="right" style={{ color: Number(d.margen) < 0 ? "#dc3545" : "#28a745" }}>
                          {fmtPct(d.margen)}
                        </td>
                      </tr>
                    ))}
                    {(() => {
                      const suma = (k) => detalleRows.reduce((t, x) => t + Number(x[k] || 0), 0);
                      const tCant = detalleRows.filter((x) => !x.pendiente).reduce((t, x) => t + Number(x.cantidad || 0), 0);
                      const tPend = detalleRows.filter((x) => x.pendiente).reduce((t, x) => t + Number(x.cantidad || 0), 0);
                      const tVenta = suma("importe");
                      const tIva = suma("iva");
                      const tCosto = suma("costoTotal");
                      const tGD = suma("gastoDirecto");
                      const tCom = suma("comision");
                      const tGG = suma("gastoGeneral");
                      const tUtil = tVenta + tIva - tCosto - tGD - tCom - tGG;
                      const r = detalleRenglon;
                      const tol = 0.05 + detalleRows.length * 0.01;
                      const cuadra =
                        r &&
                        Math.abs(tVenta - Number(r.ventaTotal || 0)) < tol &&
                        Math.abs(tCosto - Number(r.costoTotal || 0)) < tol &&
                        Math.abs(tCom - Number(r.comision || 0)) < tol &&
                        Math.abs(tUtil - Number(r.utilidad || 0)) < tol;
                      return (
                        <>
                          <tr style={{ borderTop: "2px solid #333", fontWeight: "bold" }}>
                            <td colSpan={3} align="right">Suma del desglose</td>
                            <td align="right">
                              {new Intl.NumberFormat("en-US").format(tCant)}
                              {tPend > 0 ? <span style={{ color: "#856404" }}> +{new Intl.NumberFormat("en-US").format(tPend)} pend.</span> : null}
                            </td>
                            <td colSpan={3}></td>
                            <td align="right">{fmt(tVenta)}</td>
                            <td align="right">{fmt(tIva)}</td>
                            <td></td>
                            <td align="right">{fmt(tCosto)}</td>
                            <td align="right">{fmt(tGD)}</td>
                            <td align="right">{fmt(tCom)}</td>
                            <td align="right">{fmt(tGG)}</td>
                            <td align="right" style={{ color: tUtil < 0 ? "#dc3545" : "#28a745" }}>{fmt(tUtil)}</td>
                            <td align="right" style={{ color: tUtil < 0 ? "#dc3545" : "#28a745" }}>
                              {fmtPct(tVenta + tIva > 0 ? (tUtil / (tVenta + tIva)) * 100 : 0)}
                            </td>
                          </tr>
                          {r ? (
                            <tr style={{ fontSize: 12, color: "#6c757d" }}>
                              <td colSpan={3} align="right">Renglón del reporte</td>
                              <td align="right">
                                {new Intl.NumberFormat("en-US").format(r.cantidadTotal)}
                                {Number(r.pendienteSurtir) > 0 ? ` +${new Intl.NumberFormat("en-US").format(r.pendienteSurtir)} pend.` : ""}
                              </td>
                              <td colSpan={3}></td>
                              <td align="right">{fmt(r.ventaTotal)}</td>
                              <td align="right">{fmt(r.ivaTotal)}</td>
                              <td></td>
                              <td align="right">{fmt(r.costoTotal)}</td>
                              <td align="right">{fmt(r.gastoDirecto)}</td>
                              <td align="right">{fmt(r.comision)}</td>
                              <td align="right">{fmt(r.gastoGeneral)}</td>
                              <td align="right">{fmt(r.utilidad)}</td>
                              <td align="right">
                                {fmtPct(r.margen)}{" "}
                                {cuadra ? (
                                  <span style={{ color: "#28a745" }} title="El desglose suma exactamente el renglón">✓</span>
                                ) : (
                                  <span style={{ color: "#dc3545" }} title="El desglose NO suma el renglón: revisar">✗</span>
                                )}
                              </td>
                            </tr>
                          ) : null}
                        </>
                      );
                    })()}
                  </tbody>
                </Table>
              )}
            </ModalBody>
          </Modal>
          <HistorialCostoModal
            isOpen={modalCosto}
            toggle={() => setModalCosto(!modalCosto)}
            historial={historialCosto}
            venta={ventaCosto}
            loading={costoLoading}
          />
        </div>
      ) : undefined}
    </>
  );
}

export default ReporteArticulosSurtidos;
